import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  ReactNode,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { LoadingState } from "@/components/brand";
import { applyExhibitFormat } from "../../api/OpenAIApi";
import { IndividualsAPI } from "../../api/individuals";
import { getNumberingFormats } from "../../helpers/string_helpers";
import useFirestoreCollection from "../../hooks/useFirestoreCollection";
import useFirestoreDocument from "../../hooks/useFirestoreDocument";
import { activeClientSelectors } from "../../redux/client-case/selectors";
import {
  ClientState,
  updateClientCaseData
} from "../../redux/clients/clientsSlice";
import { ExhibitListFormat, ExhibitStyle } from "../../types/exhibit_formats";
import { DATABASE } from "../../types/tables-data";
import { NewBadge } from "../common/DocumentRowStatus";
import AddNewFormatModal from "./AddNewFormatModal";
import { updateExhibitListFormat } from "../../redux/client-case/activeClientSlice";

interface FormatCardProps {
  format: ExhibitListFormat;
  isSelected?: boolean;
  onClick: (formatId: string) => void;
}

export const FormatCard = forwardRef<HTMLDivElement, FormatCardProps>(
  ({ format, isSelected = false, onClick }, ref) => {
    const [mainGroup, category, docType, bundle] = format.style ?? [];

    // Helper to render prefix + numbering
    const renderWithPrefix = (
      style: ExhibitStyle | undefined,
      numbering: string | undefined
    ) => {
      if (!style) return "";
      const { prefix } = style;
      return `${prefix && prefix !== "" ? `${prefix} ` : ""}${numbering ?? ""}`;
    };

    return (
      <div
        ref={ref}
        role="button"
        aria-pressed={isSelected}
        className={cn(
          "relative h-31 w-24 cursor-pointer rounded-lg ring-1 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
          isSelected
            ? "bg-accent-soft ring-primary"
            : "bg-elevated ring-line-2 hover:ring-line-4"
        )}
        onClick={() => onClick(format?.id ?? "")}
        tabIndex={isSelected ? 0 : -1}
      >
        {format.isNew && (
          <div className="absolute top-1 right-1 z-1">
            <NewBadge />
          </div>
        )}
        <div className="flex flex-col gap-3 px-2 py-4">
          {/* Superclass 1 */}
          <div className="flex h-2 w-full items-center gap-2">
            <span className="text-sm font-medium text-fg">
              {renderWithPrefix(mainGroup, getNumberingFormats(mainGroup)?.[0])}
            </span>
            <div className="h-1 flex-1 bg-accent-seed" />
          </div>

          {/* Category 1 */}
          <div className="flex h-2 w-full items-center justify-end gap-2">
            <span className="text-xs font-medium text-fg-2">
              {renderWithPrefix(category, getNumberingFormats(category)?.[0])}
            </span>
            <div className="h-1 w-3/5 bg-fill-4" />
          </div>

          {/* Category 2 */}
          <div className="flex h-2 w-full items-center justify-end gap-2">
            <span className="text-xs font-medium text-fg-2">
              {renderWithPrefix(category, getNumberingFormats(category)?.[1])}
            </span>
            <div className="h-1 w-3/5 bg-fill-4" />
          </div>

          {/* Document */}
          <div className="flex h-2 w-full items-center justify-end gap-2">
            <span className="text-xs font-medium text-fg-2">
              {renderWithPrefix(docType, getNumberingFormats(docType)?.[0])}
            </span>
            <div className="h-1 max-w-2/5 flex-1 bg-fill-4" />
          </div>

          {/* Superclass 2 */}
          <div className="flex h-2 w-full items-center gap-2">
            <span className="text-sm font-medium text-fg">
              {renderWithPrefix(mainGroup, getNumberingFormats(mainGroup)?.[1])}
            </span>
            <div className="h-1 flex-1 bg-accent-seed" />
          </div>
        </div>
      </div>
    );
  }
);

FormatCard.displayName = "FormatCard";

export const ExhibitListFormatPopover = ({
  children,
  onProcessingChange
}: {
  children: ReactNode;
  onProcessingChange?: (processing: boolean) => void;
}) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { documents: formats, saveDocument: saveFormat } =
    useFirestoreCollection<ExhibitListFormat>(DATABASE.EXHIBIT_LIST_FORMATS, {
      listen: true,
      useLawyerAccessMap: true,
      enableToast: true
    });

  // Kept for its read listener (onSnapshot dispatches client data to redux).
  useFirestoreDocument<ClientState>(
    DATABASE.INDIVIDUALS,
    id!,
    true,
    undefined,
    true
  );

  const caseExhibitListFormat = useSelector(
    activeClientSelectors.selectExhibitListFormat
  );

  const selectedCardRef = useRef<HTMLDivElement | null>(null);

  const handleSelectFormat = async (formatId: string) => {
    setIsProcessing(true);
    onProcessingChange?.(true);
    // Close the popover immediately so the user just sees a single, smooth
    // table loading state instead of the popover spinner followed by the
    // table re-rendering.
    setIsPopoverOpen(false);
    try {
      dispatch(updateExhibitListFormat(formatId));
      dispatch(
        updateClientCaseData({
          id,
          exhibitListFormat: formatId
        })
      );

      await IndividualsAPI.updateIndividual(id!, {
        exhibitListFormat: formatId
      });
      await applyExhibitFormat(id!, formatId);

      const selectedFormat = formats?.find((f) => f.id === formatId);
      if (selectedFormat?.isNew) {
        await saveFormat({ id: selectedFormat.id, isNew: false });
      }
    } catch (e) {
      // On failure, release the loading state immediately (the parent otherwise
      // waits for the async job status, which will never arrive).
      onProcessingChange?.(false);
      throw e;
    } finally {
      setIsProcessing(false);
      // Note: on success we intentionally do NOT clear the parent loading state
      // here. `applyExhibitFormat` only queues the async renumbering job; the
      // parent keeps the "Setting up exhibit format" panel until the job's
      // Firestore status settles, so the table is revealed exactly once.
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    // Keep the popover open when modal is closed
    setIsPopoverOpen(true);
  };

  const handleAddNewFormat = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
    setIsPopoverOpen(false);
  };

  const handlePopoverClose = () => {
    if (!isModalOpen) {
      setIsPopoverOpen(false);
    }
  };

  const isScrollable = (formats ?? []).length > 6;

  const sortedFormats = useMemo(() => {
    const list = (formats ?? []).slice();
    return list.sort((a, b) => {
      if (a.index !== undefined && b.index !== undefined) {
        return a.index - b.index;
      }
      if (a.index !== undefined) return -1;
      if (b.index !== undefined) return 1;
      return 0;
    });
  }, [formats]);

  useEffect(() => {
    if (!isPopoverOpen) return;
    if (isProcessing) return;
    if (!caseExhibitListFormat) return;
    if (sortedFormats.length === 0) return;

    const el = selectedCardRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      el.scrollIntoView({ block: "center", inline: "nearest" });
      el.focus?.();
    });
  }, [
    caseExhibitListFormat,
    isPopoverOpen,
    isProcessing,
    sortedFormats.length
  ]);

  return (
    <>
      <Popover
        open={isPopoverOpen}
        onOpenChange={(open) => {
          if (open) setIsPopoverOpen(true);
          else handlePopoverClose();
        }}
      >
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          align="start"
          className="max-h-[80vh] w-auto gap-0 overflow-y-hidden rounded-xl p-4"
        >
          <div className="mb-3">
            <p className="text-base font-medium text-fg">
              Exhibit Numbering Format
            </p>
            <p className="text-sm text-fg-3">
              Choose how exhibits are numbered across your packet.
            </p>
          </div>
          <div>
            {isProcessing ? (
              <div className="mb-4 h-30">
                <LoadingState
                  size="xl"
                  className="h-full"
                  text="Applying format..."
                />
              </div>
            ) : (
              <div
                className={cn(
                  "mb-4 max-h-65 p-1",
                  isScrollable ? "overflow-y-scroll pr-2" : "overflow-y-hidden"
                )}
              >
                <div className="mb-4 grid grid-cols-3 gap-4">
                  {sortedFormats.map((format, idx) => (
                    <FormatCard
                      key={idx}
                      format={format}
                      onClick={handleSelectFormat}
                      isSelected={format.id === caseExhibitListFormat}
                      ref={
                        format.id === caseExhibitListFormat
                          ? selectedCardRef
                          : undefined
                      }
                    />
                  ))}
                </div>
              </div>
            )}
            <div
              className={cn(
                "flex justify-end border-t border-line-1 pt-3",
                isScrollable && "pr-2"
              )}
            >
              <Button
                variant="secondary"
                onClick={handleAddNewFormat}
                disabled={isProcessing}
              >
                <Plus />
                Add New Format
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      <AddNewFormatModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onFormatCreated={handleSelectFormat}
      />
    </>
  );
};
